import { getErrorMessage } from "@entrilia/shared/api/axiosHelper";
import type { ApiError } from "@entrilia/shared/api/types";
import type { WorkflowType } from "@entrilia/shared/api/workflowTypes";

export interface DocumentPollingSession {
  sourceId: string;
  startedAtMs: number;
  inFlight: boolean;
  loggedError: boolean;
  sessionId: number;
}

export interface StartedDocumentWorkflow {
  workflowId: string;
  templateId?: string;
  workflowType: WorkflowType;
}

export type DocumentPollResult =
  { kind: "Started"; workflow: StartedDocumentWorkflow } | { kind: "NotFound" } | { kind: "Error"; error: ApiError };

interface DocumentPollingCallbacks {
  onQueueTimeout: (fileId: string) => void;
  onWorkflowStarted: (fileId: string, workflow: StartedDocumentWorkflow) => void;
  onError: (error: ApiError) => void;
}

interface DocumentPollingOptions {
  pollBySourceId: (sourceId: string) => Promise<DocumentPollResult>;
  onQueueTimeout: DocumentPollingCallbacks["onQueueTimeout"];
  onWorkflowStarted: DocumentPollingCallbacks["onWorkflowStarted"];
  onError: DocumentPollingCallbacks["onError"];
  now: () => number;
  timeoutMs: number;
  maxConcurrency: number;
}

export interface DocumentPollingScheduler {
  upsert: (fileId: string, sourceId: string) => void;
  remove: (fileId: string) => void;
  clear: () => void;
  tick: () => void;
  getSessions: () => ReadonlyMap<string, DocumentPollingSession>;
}

export const createDocumentPollingScheduler = (options: DocumentPollingOptions): DocumentPollingScheduler => {
  let nextSessionId = 0;
  const sessions = new Map<string, DocumentPollingSession>();

  const pollSessionAsync = async (fileId: string, expectedSessionId: number, sourceId: string): Promise<void> => {
    try {
      const result = await options.pollBySourceId(sourceId);
      const currentSession = sessions.get(fileId);
      if (!currentSession || currentSession.sessionId !== expectedSessionId) {
        return;
      }

      if (result.kind === "Started") {
        sessions.delete(fileId);
        options.onWorkflowStarted(fileId, result.workflow);
        return;
      }

      if (result.kind === "Error" && !currentSession.loggedError) {
        currentSession.loggedError = true;
        options.onError(result.error);
      }
    } catch (error) {
      options.onError({ message: getErrorMessage(error) });
    } finally {
      const currentSession = sessions.get(fileId);
      if (currentSession && currentSession.sessionId === expectedSessionId) {
        currentSession.inFlight = false;
      }
    }
  };

  const tick = (): void => {
    const now = options.now();

    for (const [fileId, session] of sessions) {
      if (now - session.startedAtMs < options.timeoutMs) {
        continue;
      }

      sessions.delete(fileId);
      options.onQueueTimeout(fileId);
    }

    let inFlightCount = 0;
    for (const session of sessions.values()) {
      if (session.inFlight) {
        inFlightCount++;
      }
    }

    const availableSlots = options.maxConcurrency - inFlightCount;
    if (availableSlots <= 0) {
      return;
    }

    let picked = 0;
    for (const [fileId, session] of sessions) {
      if (picked >= availableSlots) {
        break;
      }

      if (session.inFlight) {
        continue;
      }

      session.inFlight = true;
      picked++;
      void pollSessionAsync(fileId, session.sessionId, session.sourceId);
    }
  };

  return {
    upsert: (fileId: string, sourceId: string): void => {
      sessions.set(fileId, {
        sourceId,
        startedAtMs: options.now(),
        inFlight: false,
        loggedError: false,
        sessionId: nextSessionId++,
      });
    },
    remove: (fileId: string): void => {
      sessions.delete(fileId);
    },
    clear: (): void => {
      sessions.clear();
    },
    tick,
    getSessions: (): ReadonlyMap<string, DocumentPollingSession> => sessions,
  };
};
